/** Presentation-only vertical workflow progress (shared UI, domain-agnostic). */
export type WorkflowStepStatus = 'pending' | 'active' | 'done' | 'failed' | 'skipped'

export type WorkflowStep = {
  id: string
  label: string
  detail?: string
  status: WorkflowStepStatus
}

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

function stepMarkerClass(status: WorkflowStepStatus): string {
  switch (status) {
    case 'done':
      return 'border-emerald-500 bg-emerald-500 text-white'
    case 'active':
      return 'border-sky-500 bg-sky-50 text-sky-700 ring-2 ring-sky-200'
    case 'failed':
      return 'border-red-500 bg-red-50 text-red-700'
    case 'skipped':
      return 'border-slate-200 bg-slate-50 text-slate-400'
    default:
      return 'border-slate-300 bg-white text-slate-400'
  }
}

function stepLabelClass(status: WorkflowStepStatus): string {
  switch (status) {
    case 'done':
      return 'text-slate-800 font-medium'
    case 'active':
      return 'text-slate-900 font-semibold'
    case 'failed':
      return 'text-red-700 font-medium'
    case 'skipped':
      return 'text-slate-400'
    default:
      return 'text-slate-500'
  }
}

function markerSymbol(status: WorkflowStepStatus): string {
  switch (status) {
    case 'done':
      return '✓'
    case 'active':
      return '…'
    case 'failed':
      return '!'
    default:
      return ''
  }
}

export function WorkflowStepProgress({
  steps,
  ariaLabel = 'Ход выполнения',
  className,
}: {
  steps: WorkflowStep[]
  ariaLabel?: string
  className?: string
}) {
  if (steps.length === 0) return null

  return (
    <ol className={cx('space-y-0', className)} aria-label={ariaLabel}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1
        return (
          <li
            key={step.id}
            className="flex gap-3"
            aria-current={step.status === 'active' ? 'step' : undefined}
          >
            <div className="flex flex-col items-center">
              <span
                className={cx(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                  stepMarkerClass(step.status),
                )}
                aria-hidden
              >
                {markerSymbol(step.status)}
              </span>
              {!isLast ? (
                <span
                  className={cx(
                    'my-1 w-px flex-1 min-h-[1.25rem]',
                    step.status === 'done' ? 'bg-emerald-300' : 'bg-slate-200',
                  )}
                  aria-hidden
                />
              ) : null}
            </div>
            <div className={cx('pb-4 min-w-0', isLast && 'pb-0')}>
              <div className={cx('text-sm', stepLabelClass(step.status))}>{step.label}</div>
              {step.detail ? <p className="mt-0.5 text-xs text-slate-500">{step.detail}</p> : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
